import { Grid, Text, Stack, Group, Card } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { Button, CopyButton, DateCell } from "@tfg/ui"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { queryClient } from "~/backend/queryClient"
import {
  findCertificateRequestsForSupplierQueryKey,
  FindCertificateRequestsForSupplierResponseDto,
  useUpdateManyCertificateRequests,
  certificateRequestDtoStatusEnum,
} from "~/gen/api"
import { CertificateRequestStatusBadge } from "~/pages/Brand/Certificates/components/CertificateRequestStatusBadge"
import { useCertificateRequestsBatchModalContext } from "~/pages/SupplierPortal/TraceabilityRequests/Modals/certificateRequestsBatchModal/store/CertificateRequestsBatchModalContext"
import { CertificateRequestBatchPopover } from "~/pages/SupplierPortal/TraceabilityRequests/components/CertificateRequestBatchPopover"
import { CertificateRequestProductionSite } from "~/pages/SupplierPortal/TraceabilityRequests/components/CertificateRequestProductionSite"
import { CertificateRequestPurchaseOrder } from "~/pages/SupplierPortal/TraceabilityRequests/components/CertificateRequestPurchaseOrder"
import { CertificatesSelect } from "~/pages/SupplierPortal/TraceabilityRequests/components/CertificateSelect"
import { DeleteCertificateRequestModal } from "~/pages/SupplierPortal/TraceabilityRequests/components/DeleteCertificateRequestModal"
import { useAuth } from "~/store/auth-context.tsx"
import { useGetOperations } from "~/utils/operations"

interface Props {
  certificateRequest: FindCertificateRequestsForSupplierResponseDto
  setTransferCertificateRequestId: (id: string | undefined) => void
  openTransferModal: () => void
}

export const CertificateRequest = ({
  certificateRequest,
  setTransferCertificateRequestId,
  openTransferModal,
}: Props) => {
  const { isAdmin } = useAuth()
  const displayedCertificateRequest = certificateRequest
  const [certificateId, setCertificateId] = useState<string | null>(
    displayedCertificateRequest.certificateId
  )

  //Necessary for live update after query invalidation in batch update
  useEffect(() => {
    setCertificateId(displayedCertificateRequest.certificateId)
  }, [displayedCertificateRequest.certificateId])

  const { t } = useTranslation(["traceability"])
  const activity = useGetOperations()[displayedCertificateRequest.activityName]

  const { mutate: updateCertificateRequest } = useUpdateManyCertificateRequests(
    {
      mutation: {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: findCertificateRequestsForSupplierQueryKey(),
          })
        },
      },
    }
  )

  const handleSubmit = () => {
    updateCertificateRequest({
      data: {
        ids: [certificateRequest.id, ...certificateRequest.aggregatedIds],
        update: {
          status: "SUBMITTED",
        },
      },
    })
  }

  const handleUpdate = (newCertificateId: string | null) => {
    updateCertificateRequest({
      data: {
        ids: [certificateRequest.id, ...certificateRequest.aggregatedIds],
        update: {
          certificateId: newCertificateId,
        },
      },
    })
  }

  const [
    openedDeleteModal,
    { open: openDeleteModal, close: closeDeleteModal },
  ] = useDisclosure(false)

  const productionSiteId = displayedCertificateRequest.productionSite?.id

  const [opened, { close: closePopover, open: openPopover }] =
    useDisclosure(false)

  const { openBatchModal } = useCertificateRequestsBatchModalContext()

  return (
    <Card>
      <Grid pos="relative">
        <Grid.Col span={4}>
          <Stack gap="sm">
            {isAdmin && (
              <CopyButton
                label="CertificateRequest Id"
                size="xs"
                copyableText={displayedCertificateRequest.id}
                admin={isAdmin}
              />
            )}
            <Text fw={700} fz="sm">
              {displayedCertificateRequest.type.name}
            </Text>
            <CertificateRequestProductionSite
              productionSite={displayedCertificateRequest.productionSite}
            />
            {displayedCertificateRequest.type.category === "TRANSACTION" && (
              <CertificateRequestPurchaseOrder
                certificateRequest={displayedCertificateRequest}
              />
            )}
            <Group>
              <Text size="sm">{t("certificates.activityName")}</Text>
              <Group gap="xs">
                {activity.icon && <activity.icon size={20} />}
                <Text size="sm">{activity.name}</Text>
              </Group>
            </Group>
            <Group>
              <Text fw={700} fz="sm">
                {t("certificates.labels.maxIssueDate")}
              </Text>
              <DateCell date={displayedCertificateRequest.maxIssueDate} />
            </Group>
          </Stack>
        </Grid.Col>
        <Grid.Col span={6}>
          <Stack gap="sm">
            <Stack>
              <CertificateRequestBatchPopover
                target={
                  <Stack>
                    <CertificatesSelect
                      certificateId={certificateId}
                      productionSiteId={productionSiteId}
                      onCertificateSelected={(certificateId) => {
                        setCertificateId(certificateId)

                        handleUpdate(certificateId)

                        if (
                          certificateId &&
                          displayedCertificateRequest.type.category ===
                            "TRANSACTION"
                        ) {
                          openPopover()
                        }
                      }}
                      typeId={displayedCertificateRequest.type.id}
                      disabled={displayedCertificateRequest.status !== "OPENED"}
                    />
                  </Stack>
                }
                onClick={() => {
                  if (certificateId) {
                    closePopover()
                    openBatchModal({
                      certificateId,
                      certificateRequestId: displayedCertificateRequest.id,
                    })
                  }
                }}
                opened={opened}
                close={closePopover}
              />
            </Stack>
          </Stack>
        </Grid.Col>
        <Grid.Col span={2}>
          <Stack gap="sm">
            {displayedCertificateRequest.status === "OPENED" && (
              <Stack>
                <Button disabled={!certificateId} onClick={handleSubmit}>
                  {t("certificates.actions.submit")}
                </Button>
                <Button
                  onClick={() => {
                    setTransferCertificateRequestId(
                      displayedCertificateRequest.id
                    )
                    openTransferModal()
                  }}
                >
                  {t("certificates.actions.transfer")}
                </Button>
                {isAdmin && (
                  <>
                    <Button
                      color="violet"
                      variant="secondary"
                      onClick={() => {
                        openDeleteModal()
                      }}
                    >
                      {t("certificates.actions.delete")}
                    </Button>
                    <DeleteCertificateRequestModal
                      opened={openedDeleteModal}
                      close={closeDeleteModal}
                      certificateRequest={certificateRequest}
                    />
                  </>
                )}
              </Stack>
            )}
            <CertificateRequestStatusBadge
              certificateRequestStatus={displayedCertificateRequest.status}
              userType="supplier"
            />

            {!!displayedCertificateRequest.rejectionMessage &&
              displayedCertificateRequest.status ===
                certificateRequestDtoStatusEnum.REJECTED && (
                <Text c="red">
                  {displayedCertificateRequest.rejectionMessage}
                </Text>
              )}
          </Stack>
        </Grid.Col>
      </Grid>
    </Card>
  )
}
